import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Crown, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TIER_ACCENT = {
  free: "border-slate-700",
  bronze: "border-amber-700/60",
  silver: "border-slate-400/50",
  gold: "border-amber-500 ring-2 ring-amber-500/40",
};

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/tiers").then(({ data }) => setTiers(data)); }, []);

  const subscribe = async (tier) => {
    if (!user) { openAuth("register"); toast.info("Create a business account to subscribe"); return; }
    if (user.role !== "business") { toast.error("Only business accounts can subscribe"); return; }
    if (tier.id === "free") { navigate("/dashboard"); return; }
    setLoadingKey(tier.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="font-mono-accent text-xs uppercase tracking-widest text-amber-400">For businesses & sole traders</span>
        <h1 className="font-head font-black text-4xl sm:text-5xl mt-3 text-slate-50">Grow your call-out business</h1>
        <p className="text-slate-400 mt-3">Customers use MotorPro free. You pay one simple monthly fee for more visibility, more leads, more jobs. Cancel anytime.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
        {tiers.map((tier) => (
          <div key={tier.id} data-testid={`subscription-tier-${tier.id}`}
            className={`relative bg-slate-900/70 backdrop-blur border rounded-2xl p-6 flex flex-col ${TIER_ACCENT[tier.id]}`}>
            {tier.id === "gold" && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-yellow-600 text-slate-950 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1">
                <Crown size={12} /> Most Popular
              </div>
            )}
            <div className="flex items-center gap-2">
              <h3 className="font-head font-extrabold text-2xl text-slate-50">{tier.name}</h3>
              {tier.id === "gold" && <Crown size={18} className="text-amber-500" />}
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-head font-black text-4xl text-slate-50">£{tier.price}</span>
              <span className="text-slate-500 text-sm">/mo</span>
            </div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={tier.id === "gold" ? "subscribe-button-gold" : `subscribe-button-${tier.id}`}
              onClick={() => subscribe(tier)}
              disabled={loadingKey === tier.lookup_key}
              className={`w-full mt-6 rounded-xl font-bold h-11 ${tier.id === "gold" ? "bg-amber-500 hover:bg-amber-600 text-slate-950" : "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"}`}>
              {loadingKey === tier.lookup_key ? "Redirecting..." : tier.id === "free" ? "Start free" : <><Zap size={15} className="mr-1.5" /> Subscribe</>}
            </Button>
          </div>
        ))}
      </div>

      <p className="text-center text-slate-500 text-xs mt-8">Secure payments via Stripe · Test mode · Use card 4242 4242 4242 4242</p>
    </div>
  );
}
